/**
 * Utility functions for handling date conversions in API responses
 */

/**
 * Safely converts a date value (string or Date) to a Date object
 * Returns undefined for invalid dates to avoid "Invalid Date" display issues
 * Handles timezone issues to prevent dates from showing as previous day
 */
export const convertDateField = (dateValue: any): Date | undefined => {
  // Handle null/undefined explicitly
  if (dateValue === null || dateValue === undefined || dateValue === '') {
    return undefined;
  }
  
  // If it's already a Date object, validate it
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? undefined : dateValue;
  }
  
  // Try to convert string to Date with timezone handling
  try {
    let date: Date;
    const dateStr = String(dateValue);
    
    // Handle different date formats to prevent timezone issues
    if (dateStr.includes('T') && (dateStr.includes('Z') || dateStr.includes('+'))) {
      // Full ISO string with timezone - parse normally
      date = new Date(dateStr);
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // YYYY-MM-DD format - treat as local date to avoid timezone shift
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      // YYYY-MM-DD HH:mm:ss format - treat as local datetime
      const [datePart, timePart] = dateStr.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      date = new Date(year, month - 1, day, hours, minutes, seconds);
    } else {
      // Other formats - parse normally but be more permissive
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date encountered:', dateValue);
      return undefined;
    }
    return date;
  } catch (error) {
    console.warn('Failed to parse date:', dateValue, error);
    return undefined;
  }
};

/**
 * Converts multiple date fields in an object
 * Useful for converting API response objects
 */
export const convertDateFields = <T extends Record<string, any>>(
  obj: T, 
  dateFields: (keyof T)[]
): T => {
  const result = { ...obj };
  dateFields.forEach(field => {
    if (obj[field]) {
      result[field] = convertDateField(obj[field]) as any;
    }
  });
  return result;
};

/**
 * Standard date fields commonly found in task objects
 */
export const COMMON_DATE_FIELDS = [
  'identifiedDate',
  'completionDate', 
  'createdAt',
  'updatedAt'
];

/**
 * Converts common task date fields from strings to Date objects
 * Type-safe helper specifically for task objects
 * Preserves effort dates as strings to avoid timezone conversion issues
 */
export const convertTaskDates = <T extends Record<string, any>>(task: T): T => {
  // First convert the main task date fields
  const convertedTask = convertDateFields(task, COMMON_DATE_FIELDS as (keyof T)[]);
  
  // For efforts and other arrays, preserve the date strings as-is to avoid timezone issues
  // The formatDateForDisplay function will handle the timezone-safe conversion
  
  return convertedTask;
};

/**
 * Generate a PST timestamp for filename use
 * @returns YYYY_MM_DD formatted date string in PST timezone
 */
export function generatePSTDateString(): string {
  const now = new Date();
  
  // Convert to PST (UTC-8) or PDT (UTC-7) depending on daylight saving time
  // Use 'America/Los_Angeles' timezone to handle DST automatically
  const pstDate = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
  
  const year = pstDate.getFullYear();
  const month = (pstDate.getMonth() + 1).toString().padStart(2, '0');
  const day = pstDate.getDate().toString().padStart(2, '0');
  
  return `${year}_${month}_${day}`;
}

/**
 * Generate a PST timestamp for filename use with time included
 * @returns YYYY_MM_DD_HHMM formatted datetime string in PST timezone  
 */
export function generatePSTTimestampString(): string {
  const now = new Date();
  
  // Convert to PST (UTC-8) or PDT (UTC-7) depending on daylight saving time
  const pstDate = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
  
  const year = pstDate.getFullYear();
  const month = (pstDate.getMonth() + 1).toString().padStart(2, '0');
  const day = pstDate.getDate().toString().padStart(2, '0');
  const hours = pstDate.getHours().toString().padStart(2, '0');
  const minutes = pstDate.getMinutes().toString().padStart(2, '0');
  
  return `${year}_${month}_${day}_${hours}${minutes}`;
}

/**
 * Normalizes a date to YYYY-MM-DD format (date-only, no time)
 * Useful for date-only fields that shouldn't include time/timezone information
 * 
 * @param dateValue - Date string or Date object to normalize
 * @returns YYYY-MM-DD formatted string, or undefined if invalid
 */
export function normalizeDateToDateOnly(dateValue: string | Date | undefined): string | undefined {
  if (!dateValue) return undefined;
  
  let date: Date;
  
  if (dateValue instanceof Date) {
    if (isNaN(dateValue.getTime())) return undefined;
    date = dateValue;
  } else {
    const dateStr = String(dateValue);
    // If it's already in YYYY-MM-DD format, return as-is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    // Otherwise parse it
    date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
  }
  
  // Extract date components and format as YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Normalizes a date to YYYY-MM-DD using UTC components (no timezone shifting).
 * Use when storing/displaying dates that should be interpreted as calendar dates in UTC.
 *
 * @param dateValue - Date string (ISO or YYYY-MM-DD) or Date object
 * @returns YYYY-MM-DD in UTC, or undefined if invalid
 */
export function normalizeToUTCDateString(dateValue: string | Date | undefined): string | undefined {
  if (!dateValue) return undefined;

  let date: Date;

  if (dateValue instanceof Date) {
    if (isNaN(dateValue.getTime())) return undefined;
    date = dateValue;
  } else {
    const dateStr = String(dateValue).trim();
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Already YYYY-MM-DD: parse as UTC noon so we preserve the date
      const [y, m, d] = dateStr.split('-').map(Number);
      date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
      if (isNaN(date.getTime())) return undefined;
      return dateStr;
    }
    date = new Date(dateStr);
    if (isNaN(date.getTime())) return undefined;
  }

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date for display using UTC (no timezone shifting).
 *
 * @param dateValue - Date string or Date object
 * @returns Formatted date string for display, or 'Unknown' if invalid
 */
export function formatDateForDisplayUTC(dateValue: string | Date | undefined): string {
  const ymd = normalizeToUTCDateString(dateValue);
  if (!ymd) return 'Unknown';
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
  if (isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, { timeZone: 'UTC' });
}

/**
 * Returns today's date as YYYY-MM-DD in UTC.
 */
export function getCurrentUTCDateString(): string {
  const d = new Date();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converts a YYYY-MM-DD string to an ISO date string at noon UTC (for API payloads).
 *
 * @param dateStr - YYYY-MM-DD string
 * @returns ISO string (e.g. 2025-03-01T12:00:00.000Z), or undefined if invalid
 */
export function yyyyMmDdToISONoonUTC(dateStr: string): string | undefined {
  if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return undefined;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
  return isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * Parses a stored *calendar-date* value (e.g. a `YYYY-MM-DD` input, or the UTC-midnight ISO
 * string a `YYYY-MM-DD` typically round-trips to once it's gone through `new Date(...)` on the
 * server and back through JSON) into a **local** Date anchored at local midnight for that same
 * calendar day.
 *
 * Use this instead of `new Date(str)` for date-only fields (invoice date, due date, etc.)
 * before handing the result to something that reads local getters — including `date-fns`'s
 * `format`, and `Date.prototype.getFullYear/getMonth/getDate` directly. `new Date("2026-09-17")`
 * (or the equivalent `"2026-09-17T00:00:00.000Z"`) is UTC midnight; reading it back with local
 * getters in any timezone behind UTC (most of North America) lands on the *previous* day, which
 * is the "selected date turns into the wrong date" bug this exists to prevent.
 */
export function parseCalendarDate(dateValue: string | Date | undefined | null): Date | undefined {
  if (!dateValue) return undefined;
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? undefined : dateValue;
  }
  const dateStr = String(dateValue).trim();
  // Matches a leading YYYY-MM-DD whether the full string is date-only or a full ISO
  // datetime/Z string — either way the leading digits are the intended calendar date.
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(date.getTime()) ? undefined : date;
  }
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Formats a date string or Date object for display, handling timezone issues properly
 * This function prevents the common UTC timezone issue where dates appear
 * to be off by one day when converted from UTC to local time
 * 
 * @param dateValue - Date string or Date object to format
 * @returns Formatted date string for display, or 'Unknown' if invalid
 */
export function formatDateForDisplay(dateValue: string | Date | undefined): string {
  if (!dateValue) return 'Unknown';
  
  // If it's already a Date object, use it directly
  if (dateValue instanceof Date) {
    if (isNaN(dateValue.getTime())) {
      console.warn('Invalid Date object:', dateValue);
      return 'Unknown';
    }
    return dateValue.toLocaleDateString();
  }
  
  try {
    // Handle timezone issues by treating the date as local if it's in YYYY-MM-DD format
    // or if it doesn't have timezone information
    let date: Date;
    const dateStr = String(dateValue);
    
    // Debug logging to see what format dates are coming in
    
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // YYYY-MM-DD format - treat as local date to avoid timezone shift
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
      // ISO string with Z (UTC) - but likely represents local date
      // Extract the date part and treat as local to avoid timezone shift
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else if (dateStr.includes('T') && (dateStr.includes('Z') || dateStr.includes('+'))) {
      // Full ISO string with timezone - parse normally
      date = new Date(dateStr);
    } else {
      // Other formats - parse normally
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateStr);
      return 'Unknown';
    }
    
    const result = date.toLocaleDateString();
    return result;
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'Unknown';
  }
}